// src/data/fishingMarks.ts
//
// Static fishing-mark data for the Redruth Sea Angling Association website.
//
// Notes:
// - Coordinates, parking, access, tides and safety details are intentionally
//   omitted until they have been checked.
// - Newly suggested venues should be reviewed by a knowledgeable local angler
//   before publication and can be marked provisional when needed.
// - Species names and descriptions can be expanded at any time without
//   changing the map or filtering components.

export type FishingMarkType =
  | "Beach"
  | "Rock"
  | "Harbour"
  | "Pier"
  | "Estuary"
  | "Headland"
  | "Coastline"
  | "Harbour and Beach";

export type Difficulty = "Easy" | "Moderate" | "Difficult";

export interface FishingMark {
  id: string;
  name: string;
  area: string;
  type: FishingMarkType;

  coordinates?: {
    lat: number;
    lng: number;
  };

  species: string[];
  targetSpecies?: string[];

  bestSeason?: string;
  bestTide?: string;
  methods?: string[];
  baits?: string[];
  lures?: string[];

  parking?: string;
  walk?: string;
  difficulty?: Difficulty;

  description: string;
  safetyNotes?: string[];
  accessNotes?: string;

  provisional?: boolean;
}

export const fishingMarks: FishingMark[] = [
  {
    id: "porthkerris",
    name: "Porthkerris",
    area: "The Lizard",
    type: "Rock",
    species: ["Mackerel", "Garfish", "Bull Huss", "Dogfish"],
    targetSpecies: ["Mackerel", "Bull Huss"],
    bestSeason: "Summer for mackerel",
    methods: ["Spinning", "Float fishing", "Bait fishing on the bottom"],
    description:
      "A very deep-water mark, particularly good for spinning for mackerel during the summer.",
  },
  {
    id: "porthoustock",
    name: "Porthoustock",
    area: "The Lizard",
    type: "Rock",
    species: ["Bass", "Garfish", "Thick-lipped Mullet", "Pollock", "Wrasse"],
    targetSpecies: ["Bass", "Pollock", "Wrasse", "Thick-lipped Mullet"],
    description:
      "A productive mixed-species venue offering both bait and lure fishing. Mullet can be caught all year round in the right conditions.",
  },
  {
    id: "pendennis-head-falmouth",
    name: "Pendennis Head",
    area: "Falmouth and Fal Estuary",
    type: "Headland",
    species: ["Bull Huss", "Dogfish", "Mackerel", "Pollock", "Wrasse"],
    targetSpecies: ["Bull Huss", "Pollock", "Wrasse"],
    methods: ["Spinning", "Bait fishing"],
    description:
      "A deep-water headland mark producing a good range of rock-fishing species.",
  },
  {
    id: "flushing",
    name: "Flushing",
    area: "Falmouth and Fal Estuary",
    type: "Estuary",
    species: ["Thornback Ray", "Bull Huss", "Whiting", "Mackerel", "Pollock"],
    targetSpecies: ["Thornback Ray", "Bull Huss", "Mackerel"],
    bestSeason:
      "Mackerel and Whiting during winter; Thornback ray and Bull huss during summer",
    description:
      "A productive estuary venue offering a varied range of seasonal species.",
  },
  {
    id: "mylor",
    name: "Mylor",
    area: "Falmouth and Fal Estuary",
    type: "Estuary",
    species: ["Mullet", "Bull Huss", "Thornback Ray"],
    targetSpecies: ["Mullet", "Thornback Ray", "Bull Huss"],
    description:
      "A productive estuary mark for mullet, bull huss and thornback ray.",
  },
  {
    id: "king-harry-ferry",
    name: "King Harry Ferry",
    area: "Falmouth and Fal Estuary",
    type: "Estuary",
    species: ["Bull Huss", "Conger", "Gilthead Bream"],
    targetSpecies: ["Bull Huss", "Conger", "Gilthead Bream"],
    description:
      "A very deep water estuary venue producing bull huss, conger and gilthead bream.",
  },
  {
    id: "newlyn-pier",
    name: "Newlyn Pier",
    area: "West Cornwall",
    type: "Pier",
    species: ["Pollock", "Pouting", "Mackerel", "Garfish", "Bull Huss", "Dogfish", "Conger", "Flounder", "Whiting"],
    targetSpecies: ["Pollock", "Mackerel", "Conger"],
    methods: ["Float fishing", "Spinning", "Bottom fishing"],
    description:
      "A harbour pier offering a broad mix of summer and bottom-fishing species.",
  },
  {
    id: "penzance-pier",
    name: "Penzance Pier",
    area: "West Cornwall",
    type: "Pier",
    species: ["Flounder", "Dogfish", "Mackerel", "Pouting", "Garfish", "Bass"],
    targetSpecies: ["Flounder", "Mackerel"],
    methods: ["Float fishing", "Spinning", "Bottom fishing"],
    description:
      "A useful all-round harbour venue for flatfish, dogfish and seasonal pelagic species.",
  },
  {
    id: "mevagissey-pier",
    name: "Mevagissey Pier",
    area: "South Cornwall",
    type: "Pier",
    species: ["Flounder", "Dogfish", "Mackerel", "Pouting", "Garfish", "Bass"],
    targetSpecies: ["Flounder", "Mackerel"],
    methods: ["Float fishing", "Spinning", "Bottom fishing"],
    description:
      "A useful all-round harbour venue for flatfish, dogfish and seasonal pelagic species.",
  },
  {
    id: "st-ives-island",
    name: "St Ives Island",
    area: "St Ives Bay",
    type: "Rock",
    species: ["Mackerel", "Whiting", "Pouting", "Dogfish", "Bass", "Turbot", "Dab", "Plaice"],
    targetSpecies: ["Mackerel", "Bass", "Turbot"],
    description:
      "A mixed-species rock mark producing pelagic fish, bottom fish and flatfish.",
  },
  {
    id: "clodgy-point-st-ives",
    name: "Clodgy Point",
    area: "St Ives Bay",
    type: "Rock",
    species: ["Dab", "Bass", "Whiting", "Turbot"],
    targetSpecies: ["Bass", "Turbot"],
    description:
      "A St Ives rock mark producing bass, whiting, dabs and turbot.",
  },
  {
    id: "godrevy",
    name: "Godrevy",
    area: "St Ives Bay",
    type: "Rock",
    species: ["Dogfish", "Wrasse", "Pollock", "Mackerel", "Garfish", "Bull Huss", "Conger"],
    targetSpecies: ["Pollock", "Mackerel", "Bull Huss"],
    description:
      "A productive rock mark for lure fishing and bottom fishing.",
  },
  {
    id: "navax-point",
    name: "Navax Point",
    area: "North Cornwall",
    type: "Rock",
    species: ["Mackerel", "Pollock", "Garfish", "Bull Huss", "Wrasse", "Dogfish"],
    targetSpecies: ["Mackerel", "Pollock"],
    methods: ["Spinning", "Bait fishing"],
    description:
      "A rock mark producing mackerel, pollock, garfish and bull huss.",
  },
  {
    id: "portreath-harbour-and-beach",
    name: "Portreath Harbour and Beach",
    area: "North Cornwall",
    type: "Harbour and Beach",
    species: ["Flounder", "Bass", "Conger", "Dogfish"],
    targetSpecies: ["Bass", "Flounder"],
    description:
      "A combined harbour and beach venue offering flatfish, bass and bottom fishing.",
  },
  {
    id: "porthtowan-beach",
    name: "Porthtowan Beach",
    area: "North Cornwall",
    type: "Beach",
    species: ["Bass", "Flounder", "Dogfish"],
    targetSpecies: ["Bass"],
    description:
      "A surf beach producing bass, flounder and dogfish.",
  },
  {
    id: "tubbys-head",
    name: "Tubby's Head",
    area: "North Cornwall",
    type: "Rock",
    species: ["Bass", "Small-eyed Ray", "Dogfish", "Bull Huss", "Weever"],
    targetSpecies: ["Bass", "Small-eyed Ray", "Bull Huss"],
    description:
      "A mixed-species rock mark casting onto sand producing bass, rays, huss and dogfish.",
  },
  {
    id: "factory-farm",
    name: "Factory Farm",
    area: "North Cornwall",
    type: "Rock",
    species: ["Bass", "Flatfish", "Small-eyed Ray", "Dogfish", "Weaver"],
    targetSpecies: ["Bass", "Small-eyed Ray"],
    description:
      "A productive rock mark casting onto sand for bass, flatfish, rays and dogfish.",
  },
  {
    id: "st-agnes-head",
    name: "St Agnes Head",
    area: "North Cornwall",
    type: "Headland",
    species: ["Mackerel", "Garfish", "Bull Huss", "Gurnard", "Weever", "Small-eyed Ray", "Turbot", "Dab", "Dogfish", "Plaice"],
    targetSpecies: ["Mackerel", "Bull Huss", "Small-eyed Ray"],
    description:
      "A varied headland mark producing pelagic fish, rays, huss and flatfish. Very deep water. Steep climb.",
  },
  {
    id: "cligga-head",
    name: "Cligga Head",
    area: "North Cornwall",
    type: "Headland",
    species: ["Dogfish", "Bull Huss", "Small-eyed Ray", "Gurnard", "Weever", "Flatfish", "Mackeral", "Smoothhound", "Plaice"],
    targetSpecies: ["Bull Huss", "Small-eyed Ray", "Smoothhound"],
    description:
      "A productive headland mark for rays, huss, dogfish, smoothhound and flatfish.",
  },
  {
    id: "rinsey-headland",
    name: "Rinsey Headland",
    area: "South West Cornwall",
    type: "Rock",
    species: ["Bass", "Pollock", "Wrasse", "Mackerel", "Small-eyed Ray", "Bull Huss", "Plaice"],
    targetSpecies: ["Bass", "Pollock", "Wrasse", "Mackerel", "Small-eyed Ray", "Bull Huss"],
    description:
      "A rocky headland mark where anglers fish from the rocks onto sand, producing bass, pollock, wrasse, mackerel, small-eyed ray and bull huss.",
    provisional: false,
  },
  {
    id: "perranporth-beach",
    name: "Perranporth Beach",
    area: "North Cornwall",
    type: "Beach",
    species: ["Bass", "Small-eyed Ray", "Smoothhound", "Dogfish"],
    targetSpecies: ["Bass", "Small-eyed Ray", "Smoothhound"],
    description:
      "A very good surf beach for bass, small-eyed ray, smoothhound and dogfish.",
  },
  {
    id: "loe-bar",
    name: "Loe Bar",
    area: "South West Cornwall",
    type: "Beach",
    species: ["Small-eyed Ray", "Spurdog", "Bull Huss", "Bass", "Conger", "Mackerel", "Smoothhound"],
    targetSpecies: ["Small-eyed Ray", "Bull Huss", "Bass"],
    description:
      "A very deep-water venue with a steeply shelving shingle bank, producing rays, huss, bass, conger, smoothhound and mackerel.",
    safetyNotes: [
      "The beach shelves steeply into deep water.",
      "Conditions and access should be checked carefully before fishing.",
    ],
  },
  {
    id: "newquay-headland-towan-head",
    name: "Newquay Headland (Towan Head)",
    area: "Newquay",
    type: "Headland",
    species: ["Smoothhound", "Bass", "Dogfish", "Mackerel", "Garfish", "Pollock", "Wrasse", "Scad", "Dab", "Plaice"],
    targetSpecies: ["Bass", "Mackerel", "Pollock"],
    methods: ["Spinning", "Bait fishing"],
    description:
      "A productive headland offering both lure and bait fishing for a broad range of species.",
  },
  {
    id: "holywell-beach",
    name: "Holywell Beach",
    area: "Newquay",
    type: "Beach",
    species: ["Bass", "Small-eyed Ray", "Smoothhound"],
    targetSpecies: ["Bass", "Small-eyed Ray", "Smoothhound"],
    description:
      "A beach with a reputation for large bass and small-eyed ray.",
  },
  {
    id: "watergate-bay-beach",
    name: "Watergate Bay Beach",
    area: "Newquay",
    type: "Beach",
    species: ["Bass", "Small-eyed Ray", "Smoothhound"],
    targetSpecies: ["Bass", "Small-eyed Ray", "Smoothhound"],
    description:
      "A very good beach for large bass, large small-eyed ray and smoothhound.",
  },
  {
    id: "fistral-beach-newquay",
    name: "Fistral Beach",
    area: "Newquay",
    type: "Beach",
    species: ["Bass", "Small-eyed Ray", "Dogfish", "Smoothhound"],
    targetSpecies: ["Bass", "Small-eyed Ray", "Smoothhound"],
    description:
      "A popular surf beach producing bass, small-eyed ray, dogfish and smoothhound.",
  },
  {
    id: "bedruthan-steps",
    name: "Bedruthan Steps",
    area: "North Cornwall",
    type: "Beach",
    species: ["Bass"],
    targetSpecies: ["Bass"],
    description:
      "A venue noted for producing large bass.",
  },
  {
    id: "trevose-head",
    name: "Trevose Head",
    area: "North Cornwall",
    type: "Headland",
    species: ["Tope", "Small-eyed Ray", "Dogfish", "Bull Huss", "Mackerel", "Pollock", "Garfish"],
    targetSpecies: ["Tope", "Small-eyed Ray", "Pollock"],
    description:
      "A varied headland mark producing tope, rays, huss, pelagic fish and flatfish.",
  },
  {
    id: "camel-estuary-padstow",
    name: "Camel Estuary",
    area: "Padstow and Camel Estuary",
    type: "Estuary",
    species: ["Flounder", "Bass", "Mullet"],
    targetSpecies: ["Flounder", "Bass"],
    bestSeason: "Flounder in winter; bass and mullet in summer",
    description:
      "An excellent winter flounder venue, with bass and mullet during the summer.",
  },
  {
    id: "hayle-estuary",
    name: "Hayle Estuary",
    area: "St Ives Bay",
    type: "Estuary",
    species: ["Bass", "Gilthead Bream", "Mullet", "Flounder"],
    targetSpecies: ["Bass", "Gilthead Bream", "Mullet"],
    description:
      "A productive sand estuary for bass, gilthead bream and several mullet species.",
  },
  {
    id: "st-just-coastline",
    name: "St Just Coastline",
    area: "West Cornwall",
    type: "Coastline",
    species: ["Pollock"],
    targetSpecies: ["Pollock"],
    methods: ["Spinning", "Lure fishing", "Float fishing"],
    description:
      "A stretch of coastline noted for large pollock taken by spinning from the shore.",
  },
  {
    id: "penryn-river",
    name: "Penryn River",
    area: "Falmouth and Fal Estuary",
    type: "Estuary",
    species: ["Flounder", "Bass", "Mullet", "Gilthead Bream"],
    targetSpecies: ["Flounder", "Bass", "Mullet", "Gilthead Bream"],
    description:
      "A productive estuary venue for flounder, bass, mullet, Gilthead Bream.",
  },
  {
    id: "stack-point-falmouth",
    name: "Stack Point",
    area: "Falmouth and Fal Estuary",
    type: "Rock",
    species: ["Bull Huss", "Dogfish", "Conger"],
    targetSpecies: ["Bull Huss", "Conger"],
    bestSeason: "Summer; especially productive after dark",
    description:
      "Rough ground. An after-dark rock mark producing bull huss, dogfish and conger.",
  },
  {
    id: "gurnards-head",
    name: "Gurnard's Head",
    area: "West Cornwall",
    type: "Headland",
    species: ["Pollock", "Mackeral", "Bull huss"],
    targetSpecies: ["Pollock"],
    methods: ["Spinning", "Lure fishing", "Float fishing", "Bottom fishing"],
    description:
      "A deep-water rocky headland and a good shore mark for pollock.",
  },
  {
    id: "helford-river-tank-tracks",
    name: "Helford River – Tank Tracks",
    area: "Helford River",
    type: "Estuary",
    species: ["Thornback Ray", "Bull Huss", "Dogfish", "Black Bream", "Red Bream", "Mackerel", "Bass"],
    targetSpecies: ["Thornback Ray", "Bull Huss", "Black Bream"],
    description:
      "One of two established Helford River marks, producing rays, huss, bream, bass and seasonal mackerel.",
  },
  {
    id: "helford-river-toll-point",
    name: "Helford River – Toll Point",
    area: "Helford River",
    type: "Estuary",
    species: ["Thornback Ray", "Bull Huss", "Dogfish", "Black Bream", "Mackerel", "Bass"],
    targetSpecies: ["Thornback Ray", "Bull Huss", "Black Bream"],
    description:
      "A productive Helford River mark offering a mixture of estuary and open-coast species.",
  },
  {
    id: "helford-river-upper-stretches",
    name: "Helford River – Upper Stretches",
    area: "Helford River",
    type: "Estuary",
    species: ["Gilthead Bream", "Flounder", "Bull Huss"],
    targetSpecies: ["Gilthead Bream", "Flounder", "Bull Huss"],
    methods: ["Bottom fishing"],
    baits: [
      "Crab for gilthead bream",
      "Worm for gilthead bream and flounder",
      "Fish bait for bull huss",
    ],
    description:
      "The upper stretches of the Helford River produce gilthead bream, flounder and bull huss. Fish on the bottom with crab or worm for gilthead bream, worm for flounder, and fish bait for huss.",
    provisional: false,
  },
  {
    id: "pedn-billy-boathouse",
    name: "Pedn Billy Boathouse",
    area: "Helford River",
    type: "Estuary",
    species: ["Gilthead Bream", "Black Bream", "Couch's Bream"],
    targetSpecies: ["Gilthead Bream", "Black Bream", "Couch's Bream"],
    methods: ["Bottom fishing"],
    baits: ["Worm", "Crab"],
    description:
      "Fish on the bottom with worm or crab for gilthead bream, black bream and couch's bream.",
    provisional: false,
  },
  {
    id: "gillan-creek",
    name: "Gillan Creek",
    area: "Helford River",
    type: "Estuary",
    species: ["Gilthead Bream", "Black Bream", "Couch's Bream"],
    targetSpecies: ["Gilthead Bream", "Black Bream", "Couch's Bream"],
    methods: ["Bottom fishing"],
    baits: ["Worm", "Crab"],
    description:
      "Fish on the bottom with worm or crab for gilthead bream, black bream and couch's bream.",
    provisional: false,
  },

  // Additional suggested venues. Review these entries locally before publication.
  {
    id: "lizard-point",
    name: "Lizard Point",
    area: "The Lizard",
    type: "Headland",
    methods: ["Bottom fishing", "Spinning"],
    species: ["Pollock", "Wrasse", "Bull Huss", "Conger", "Mackerel", "Plaice"],
    targetSpecies: ["Pollock", "Wrasse", "Plaice"],
    description:
      "A deep-water headland offering lure and bait fishing.",
    provisional: false,
  },
  {
    id: "kynance-cove",
    name: "Kynance Cove",
    area: "The Lizard",
    type: "Rock",
    species: ["Pollock", "Wrasse", "Mackerel", "Garfish"],
    targetSpecies: ["Pollock", "Wrasse"],
    bestSeason: "Calm summer conditions",
    description:
      "A scenic rock-fishing venue best approached in suitable calm conditions.",
    provisional: false,
  },
  {
    id: "coverack",
    name: "Coverack",
    area: "The Lizard",
    type: "Harbour",
    species: ["Mackerel", "Garfish", "Pollock", "Wrasse", "Bass"],
    targetSpecies: ["Mackerel", "Pollock", "Bass"],
    description:
      "A harbour and nearby rock-fishing area producing a varied range of species.",
    provisional: false,
  },
  {
    id: "porthleven-harbour",
    name: "Porthleven Harbour",
    area: "South West Cornwall",
    type: "Harbour",
    species: ["Bass", "Mullet", "Pollock", "Conger"],
    targetSpecies: ["Bass", "Mullet"],
    description:
      "A harbour and adjacent rock-fishing area for bass, mullet, pollock and conger.",
    provisional: false,
  },
  {
    id: "praa-sands",
    name: "Praa Sands",
    area: "West Cornwall",
    type: "Beach",
    species: ["Bass", "Dogfish", "Small-eyed Ray", "Smoothhound"],
    targetSpecies: ["Bass", "Small-eyed Ray", "Smoothhound"],
    description:
      "A surf beach producing bass, dogfish and small-eyed ray.",
    provisional: false,
  },
  {
    id: "marazion-beach",
    name: "Marazion Beach",
    area: "West Cornwall",
    type: "Beach",
    species: ["Bass", "Flounder", "Mullet"],
    targetSpecies: ["Bass", "Flounder"],
    description:
      "A beach venue producing bass, flounder and mullet.",
    provisional: false,
  },
  {
    id: "mousehole-harbour",
    name: "Mousehole Harbour",
    area: "West Cornwall",
    type: "Harbour",
    species: ["Mullet", "Pollock", "Garfish", "Mackerel", "Conger", "Bull huss"],
    targetSpecies: ["Mullet", "Mackerel"],
    bestSeason: "Summer",
    description:
      "A harbour venue for mullet, pollock, garfish and mackerel.",
    provisional: false,
  },
  {
    id: "sennen-cove",
    name: "Sennen Cove",
    area: "West Cornwall",
    type: "Beach",
    species: ["Bass", "Pollock", "Mackerel", "Garfish"],
    targetSpecies: ["Bass", "Pollock"],
    methods: ["Lure fishing", "Spinning", "Bait fishing"],
    description:
      "A coastal venue offering bass fishing and seasonal lure fishing.",
    provisional: false,
  },
  {
    id: "cape-cornwall",
    name: "Cape Cornwall",
    area: "West Cornwall",
    type: "Headland",
    species: ["Pollock", "Wrasse", "Bull Huss", "Conger"],
    targetSpecies: ["Pollock", "Bull Huss"],
    description:
      "A deep-water headland mark producing pollock, wrasse, huss and conger.",
    provisional: false,
  },
  {
    id: "port-quin",
    name: "Port Quin",
    area: "North Cornwall",
    type: "Rock",
    species: ["Pollock", "Wrasse", "Mackerel"],
    targetSpecies: ["Pollock", "Wrasse"],
    description:
      "A north-coast rock-fishing venue producing pollock, wrasse and mackerel.",
    provisional: false,
  },
  {
    id: "boscastle-harbour",
    name: "Boscastle Harbour",
    area: "North Cornwall",
    type: "Harbour",
    species: ["Bass", "Mullet", "Pollock"],
    targetSpecies: ["Bass", "Mullet"],
    description:
      "A harbour venue producing bass, mullet and pollock.",
    provisional: false,
  },
  {
    id: "crackington-haven",
    name: "Crackington Haven",
    area: "North Cornwall",
    type: "Beach",
    species: ["Bass", "Dogfish", "Small-eyed Ray"],
    targetSpecies: ["Bass", "Small-eyed Ray"],
    description:
      "A surf beach producing bass, dogfish and small-eyed ray.",
    provisional: false,
  },
  {
    id: "bude-breakwater",
    name: "Bude Breakwater",
    area: "North Cornwall",
    type: "Harbour",
    species: ["Mackerel", "Garfish", "Pollock", "Wrasse"],
    targetSpecies: ["Mackerel", "Pollock"],
    bestSeason: "Summer",
    description:
      "A breakwater venue producing seasonal mackerel, garfish, pollock and wrasse.",
    provisional: false,
  },
  {
    id: "charlestown-harbour",
    name: "Charlestown Harbour",
    area: "South Cornwall",
    type: "Harbour",
    species: ["Mullet", "Bass", "Pollock"],
    targetSpecies: ["Mullet", "Bass"],
    description:
      "A harbour venue producing mullet, bass and pollock.",
    provisional: false,
  },
  {
    id: "mevagissey-harbour",
    name: "Mevagissey Harbour",
    area: "South Cornwall",
    type: "Harbour",
    species: ["Mullet", "Pollock", "Garfish", "Mackerel"],
    targetSpecies: ["Mullet", "Mackerel"],
    bestSeason: "Summer",
    description:
      "A harbour venue producing mullet, pollock, garfish and mackerel.",
    provisional: false,
  },
  {
    id: "fowey-estuary",
    name: "Fowey Estuary",
    area: "South Cornwall",
    type: "Estuary",
    species: ["Bass", "Flounder", "Mullet", "Gilthead Bream"],
    targetSpecies: ["Bass", "Flounder", "Gilthead Bream"],
    description:
      "An estuary producing bass, flounder, mullet and gilthead bream.",
    provisional: false,
  },
  {
    id: "looe",
    name: "Looe",
    area: "South Cornwall",
    type: "Harbour",
    species: ["Pollock", "Mackerel", "Garfish", "Wrasse"],
    targetSpecies: ["Pollock", "Mackerel"],
    description:
      "A harbour and nearby rock-fishing area producing pollock, mackerel, garfish and wrasse.",
    provisional: false,
  },
  {
    id: "whitsand-bay",
    name: "Whitsand Bay",
    area: "South Cornwall",
    type: "Beach",
    species: ["Bass", "Dogfish", "Smoothhound", "Small-eyed Ray"],
    targetSpecies: ["Bass", "Smoothhound", "Small-eyed Ray"],
    description:
      "A long surf beach with potential for bass, smoothhound, dogfish and small-eyed ray.",
    provisional: false,
  },
  {
    id: "pentreath-beach",
    name: "Pentreath Beach",
    area: "The Lizard",
    type: "Beach",
    species: ["Bass"],
    targetSpecies: ["Bass"],
    description:
      "A bass mark fished from the rocks by casting into the surf in a mild south-westerly wind.",
    provisional: false,
  },
];

export const confirmedFishingMarks = fishingMarks.filter(
  (mark) => !mark.provisional,
);

export const provisionalFishingMarks = fishingMarks.filter(
  (mark) => mark.provisional,
);

export const confirmedFishingMarkAreas = Array.from(
  new Set(confirmedFishingMarks.map((mark) => mark.area)),
).sort();

export const confirmedFishingMarkSpecies = Array.from(
  new Set(confirmedFishingMarks.flatMap((mark) => mark.species)),
).sort();

export const fishingMarkAreas = Array.from(
  new Set(fishingMarks.map((mark) => mark.area)),
).sort();

export const fishingMarkSpecies = Array.from(
  new Set(fishingMarks.flatMap((mark) => mark.species)),
).sort();
